import { ActionIcon, Tour } from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import { IconHelp } from '@tabler/icons-react';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { captureEvent } from 'shared/analytics/capture-event';
import { useLoadGroupInvoiceTotals } from 'shared/hooks/use-load-group-invoice-totals';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';

import { useConsolidation } from './consolidation';

const TARGETED_STEPS = [
  { target: 'group-company-filter', copy: i18nKeys.GROUP.TOUR.FILTER },
  { target: 'group-computed-at', copy: i18nKeys.GROUP.TOUR.COMPUTED_AT },
  { target: 'group-balance-hero', copy: i18nKeys.GROUP.TOUR.OUTSTANDING },
  { target: 'group-by-company', copy: i18nKeys.GROUP.TOUR.BY_COMPANY },
  { target: 'group-aged-balance', copy: i18nKeys.GROUP.TOUR.AGED_BALANCE },
  { target: 'group-performance', copy: i18nKeys.GROUP.TOUR.PERFORMANCE },
];

const NAVBAR_STEPS = [
  { target: 'group-nav', copy: i18nKeys.GROUP.TOUR.NAVIGATION },
  { target: 'group-feedback', copy: i18nKeys.GROUP.TOUR.FEEDBACK },
];

interface GroupTourProps {
  companyCount: number;
}

export const GroupTour = ({ companyCount }: GroupTourProps) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [hasSeenTour, setHasSeenTour] = useLocalStorage({
    key: `recovr:group-tour-seen:${useProfile().id}`,
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const { isGroupAnalyticsLoading } = useConsolidation([]);
  const { isGroupInvoiceTotalsLoading } = useLoadGroupInvoiceTotals({ companies: [] });
  const isLoading = isGroupAnalyticsLoading || isGroupInvoiceTotalsLoading;
  const [isTourActive, { open: startTour, close: endTour }] = useDisclosure(false);
  const currentStepRef = useRef(0);
  const isMobile = useIsMobile();
  const targetedSteps = isMobile ? TARGETED_STEPS : [...TARGETED_STEPS, ...NAVBAR_STEPS];
  const stepsCount = targetedSteps.length + 1;

  useEffect(() => {
    if (hasSeenTour || isLoading || isTourActive) return;
    captureEvent(posthog, 'group:tour_started', { trigger: 'auto' });
    startTour();
  }, [hasSeenTour, isLoading, isTourActive, posthog, startTour]);

  return (
    <>
      <ActionIcon
        variant="subtle"
        color="gray"
        aria-label={t(i18nKeys.GROUP.TOUR.START)}
        disabled={isLoading}
        onClick={() => {
          captureEvent(posthog, 'group:tour_started', { trigger: 'button' });
          startTour();
        }}
      >
        <IconHelp />
      </ActionIcon>
      <Tour
        active={isTourActive}
        onStepChange={step => {
          currentStepRef.current = step;
        }}
        onClose={() => {
          if (currentStepRef.current === stepsCount - 1) {
            captureEvent(posthog, 'group:tour_completed', { steps_count: stepsCount });
          } else {
            captureEvent(posthog, 'group:tour_closed', {
              step: currentStepRef.current + 1,
              steps_count: stepsCount,
            });
          }
          endTour();
          setHasSeenTour(true);
        }}
        withCloseButton={false}
        labels={{
          skip: t(i18nKeys.GROUP.TOUR.SKIP),
          next: t(i18nKeys.GROUP.TOUR.NEXT),
          back: t(i18nKeys.GROUP.TOUR.BACK),
          close: t(i18nKeys.GROUP.TOUR.FINISH),
          stepCounter: (current, total) => `${current} / ${total}`,
        }}
      >
        <Tour.Step title={t(i18nKeys.GROUP.TOUR.WELCOME.TITLE)}>
          {t(i18nKeys.GROUP.TOUR.WELCOME.BODY, { count: companyCount })}
        </Tour.Step>
        {targetedSteps.map(({ target, copy }) => (
          <Tour.Step key={target} target={`[data-tour=${target}]`} title={t(copy.TITLE)}>
            {t(copy.BODY)}
          </Tour.Step>
        ))}
      </Tour>
    </>
  );
};
